import { useState } from "react";
import { Printer, Check, Download, Phone, Mail } from "lucide-react";
import Reveal from "./Reveal";

const SPECS = [
  { label: "Le groupe", value: "Créé en 2012 — 5 artistes : chant, guitare, basse/chant, piano, batterie" },
  { label: "Le groupe apporte", value: "Instruments et amplis (Orange Rockerverb 50, Markbass…), pédaliers, micro HF chant" },
  { label: "À prévoir côté organisateur", value: "Sonorisation adaptée au lieu, éclairage scène, 5 retours « bains de pied »" },
  { label: "Batterie", value: "Acoustique et sonorisée — praticable / plateau surélevé si possible" },
  { label: "Patch scène", value: "Basse : 1 micro · Clavier : 1 DI · Guitare : 1 micro · Chant : 1 micro HF + pied · Chœurs : 3 micros sur perches" },
  { label: "Loges", value: "Une loge fermant à clé, bouteilles d'eau sur scène, 5 repas" },
];

const SETLIST = {
  "70s": [
    "Roxanne — The Police",
    "Don't Stop Me Now — Queen",
    "Proud Mary — Creedence / Tina Turner",
    "Voulez-vous — ABBA",
    "Knock on Wood",
    "Toute la musique que j'aime — Johnny Hallyday",
    "Video Killed the Radio Star — The Buggles",
    "One Way or Another — Blondie",
    "Georgy Porgy — Toto",
    "Born to Be Wild — Steppenwolf",
    "Burning Love — Elvis Presley",
    "Whatever You Want — Status Quo",
  ],
  "80s": [
    "Eye of the Tiger — Survivor",
    "Where the Streets Have No Name — U2",
    "Un autre monde — Téléphone",
    "Je te donne — Goldman / Jones",
    "New York avec toi — Téléphone",
    "Ça c'est vraiment toi — Téléphone",
    "J'ai vu — Niagara",
  ],
  "90s": [
    "Smells Like Teen Spirit — Nirvana",
    "Allumez le feu — Johnny Hallyday",
    "Man! I Feel Like a Woman — Shania Twain",
    "The Show Must Go On — Queen",
    "J'irai où tu iras — Céline Dion & J-J Goldman",
  ],
  "2000+": [
    "Heavy Cross — Gossip",
    "Seven Nation Army — The White Stripes",
    "Set Fire to the Rain — Adele",
    "Are You Gonna Be My Girl — Jet",
    "Something's Got a Hold on Me — Etta James",
  ],
};

const STAGE_SPOTS = [
  { x: 50, y: 14, label: "Batterie — Florent", sub: "praticable surélevé" },
  { x: 24, y: 30, label: "Clavier — Thibault" },
  { x: 17, y: 62, label: "Guitare — Stéphane" },
  { x: 83, y: 62, label: "Basse — Florence" },
  { x: 50, y: 80, label: "Chant — Catherine" },
];

const Dossier = () => {
  const [era, setEra] = useState("70s");

  return (
    <section id="dossier" data-testid="tech-dossier-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-jbmono font-semibold text-amber-400 mb-4">
            Organisateurs & mairies
          </p>
          <h2 className="font-display text-2xl sm:text-4xl font-bold tracking-tight uppercase mb-3">
            Dossier <span className="text-amber-500">technique</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl text-base mb-12">
            Tout ce qu'un comité des fêtes ou un service animation doit savoir :
            plan de scène, besoins techniques et répertoire complet.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-6 items-start">
          <Reveal>
            <div data-testid="stage-plan-card" className="rounded-2xl border border-zinc-800 bg-[#161622] p-6">
              <p className="text-xs font-jbmono uppercase tracking-[0.25em] text-amber-400 mb-5">Plan de scène</p>
              <div className="relative rounded-xl border-2 border-dashed border-amber-500/30 bg-[#09090D] aspect-[4/3] overflow-hidden">
                <span className="absolute top-3 left-1/2 -translate-x-1/2 text-[10px] font-jbmono uppercase tracking-[0.3em] text-zinc-500">
                  Fond de scène
                </span>
                <span className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[10px] font-jbmono uppercase tracking-[0.3em] text-zinc-500">
                  Public
                </span>
                {STAGE_SPOTS.map((s) => (
                  <div
                    key={s.label}
                    className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1"
                    style={{ left: `${s.x}%`, top: `${s.y}%` }}
                  >
                    <span className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-500/50 flex items-center justify-center text-amber-400 text-[10px] font-bold font-jbmono">
                      {s.label.split(" — ")[0].slice(0, 2).toUpperCase()}
                    </span>
                    <span className="text-[10px] font-jbmono uppercase tracking-wider text-zinc-400 whitespace-nowrap">{s.label}</span>
                    {s.sub && (
                      <span className="text-[9px] font-jbmono uppercase tracking-wider text-amber-500/70 -mt-1">{s.sub}</span>
                    )}
                  </div>
                ))}
              </div>

              <ul className="mt-6 space-y-3">
                {SPECS.map((s) => (
                  <li key={s.label} className="flex gap-3 text-sm">
                    <Check size={16} className="text-amber-500 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-zinc-100">{s.label} : </span>
                      <span className="text-zinc-400">{s.value}</span>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-6 rounded-xl border border-zinc-800 bg-[#09090D]/60 p-4 space-y-2">
                <p className="text-xs font-jbmono uppercase tracking-widest text-zinc-500">Contacts</p>
                <p className="text-sm text-zinc-300 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="font-semibold">Général — Florence</span>
                  <a href="tel:+33660916764" className="flex items-center gap-1.5 hover:text-amber-400 transition-colors">
                    <Phone size={12} /> 06 60 91 67 64
                  </a>
                  <a href="mailto:fortyone@forty-one.fr" className="flex items-center gap-1.5 hover:text-amber-400 transition-colors">
                    <Mail size={12} /> fortyone@forty-one.fr
                  </a>
                </p>
                <p className="text-sm text-zinc-300 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="font-semibold">Technique — Stéphane</span>
                  <a href="tel:+33667206444" className="flex items-center gap-1.5 hover:text-amber-400 transition-colors">
                    <Phone size={12} /> 06 67 20 64 44
                  </a>
                </p>
              </div>

              <div className="no-print mt-7 flex flex-wrap gap-3">
                <a
                  data-testid="download-rider-button"
                  href="/docs/Fiche%20technique%20Forty%20One.docx"
                  download
                  className="flex items-center gap-2.5 bg-amber-500 hover:bg-amber-400 text-white font-bold uppercase tracking-wider text-xs rounded-full px-6 py-3 transition-colors duration-300"
                >
                  <Download size={15} />
                  Télécharger la fiche technique
                </a>
                <button
                  data-testid="print-dossier-button"
                  onClick={() => window.print()}
                  className="flex items-center gap-2.5 border border-amber-500/40 hover:bg-amber-500 hover:text-white text-amber-400 font-bold uppercase tracking-wider text-xs rounded-full px-6 py-3 transition-colors duration-300"
                >
                  <Printer size={15} />
                  Imprimer
                </button>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div data-testid="setlist-card" className="rounded-2xl border border-zinc-800 bg-[#161622] p-6">
              <p className="text-xs font-jbmono uppercase tracking-[0.25em] text-amber-400 mb-2">
                Set list — 29 titres
              </p>
              <p className="text-xs text-zinc-500 mb-5">
                Le répertoire réel du groupe, des années 70 à aujourd'hui.
              </p>
              <div className="flex flex-wrap gap-2 mb-6 no-print">
                {Object.keys(SETLIST).map((e) => (
                  <button
                    key={e}
                    data-testid={`setlist-filter-${e.replace("+", "plus")}`}
                    onClick={() => setEra(e)}
                    className={`text-xs font-jbmono uppercase tracking-wider rounded-full px-4 py-2 border transition-colors duration-300 ${
                      era === e
                        ? "bg-amber-500 text-white border-amber-500 font-bold"
                        : "border-zinc-700 text-zinc-400 hover:text-amber-400 hover:border-amber-500/40"
                    }`}
                  >
                    {e}
                    <span className="ml-1.5 opacity-60">{SETLIST[e].length}</span>
                  </button>
                ))}
              </div>
              <ul className="space-y-3 max-h-[420px] overflow-y-auto pr-2">
                {SETLIST[era].map((song, i) => (
                  <li
                    key={song}
                    data-testid={`setlist-song-${i}`}
                    className="flex items-center gap-4 rounded-xl border border-zinc-800/80 bg-[#09090D]/60 px-4 py-3"
                  >
                    <span className="font-jbmono text-amber-500 text-xs w-6">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm text-zinc-200">{song}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-xs text-zinc-500">
                La set list est adaptée à votre public et à la durée de votre événement.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Dossier;
